import { action, computed, observable, runInAction, set, toJS } from 'mobx';
import {
    addUnitsOfTime,
    autostore,
    bind,
    HttpClient,
    IAccessTokenStore,
    IDisposable,
    IMultiSelectOption,
    ITimezonePreferenceStore,
    mobxEqual,
} from 'Mri.Web.Common';
import { Log, User, UserManager, UserManagerSettings } from 'oidc-client';
import { ManageStore } from '../Manage/ManageStore';
import {DisplayViewTypes} from "../DisplayFilters/DisplayViewSelector";
import {isEqual} from "lodash";

export interface IUserColumnPreset {
    type?: string;
    name: string;
    columns: IMultiSelectOption<string | Mri.WebApi.MeasurementId>[];
    configColumns: IMultiSelectOption<string | Mri.WebApi.MeasurementId>[];
}
export class AccountStore implements IAccessTokenStore, ITimezonePreferenceStore {
    @computed
    public get timezone() {
        return this.user?.preferredTimezone;
    }

    public constructor(httpClient: HttpClient, oidcConfig: UserManagerSettings) {
        this.httpClient = httpClient;
        this.configureAutoStore(false);
        Log.logger = console;
        Log.level = Log.WARN;
        this.userManager = new UserManager(oidcConfig);
        this.userManager.events.addUserSignedOut(
            action(() => {
                this.userManager.removeUser();
                this.userStorage.user = undefined;
            })
        );

        autostore(this.userStorage, 'AccountStore.UserStorage', {
            useLocalStorage: false,
            useSessionStorage: true,
            useCustomStorage: false,
        });
    }

    public signinRedirect(state: any) {
        this.userManager.signinRedirect({
            state,
        });
    }

    @action
    public async signinRedirectCallback(): Promise<any> {
        try {
            const user = await this.userManager.signinRedirectCallback();
            if (user) {
                // Get the ApplicationUser from the database
                this.userStorage.user = await this.httpClient.get<Mri.Web.Portal.Controllers.Account.UserModel>(
                    '/api/v1/account'
                );
                this.configureAutoStore();
                return user.state;
            }
        } catch (e: any) {
            // Clearing state and trying again if less than 3 tries
            console.error(e);
            return 'error';
        }

        return undefined;
    }

    @action
    public async deleteAsync(user: Mri.Web.Portal.Controllers.Account.DeleteUserModel) {
        await this.httpClient.post(`/api/v1/account/delete`, user);

        return this.userManager.signoutRedirect();
    }

    public async renewCallback() {
        await this.userManager.signinSilentCallback();
    }

    @bind
    public async signoutRedirect() {
        return this.userManager.signoutRedirect();
    }

    @action
    public async loadUserAsync() {
        try {
            const user = await this.userManager.getUser();
            if (user) {
                // Get the ApplicationUser from the database,
                const promise = this.tryLoadUserAsync();

                // However only await if not cached
                if (!this.userStorage.user) {
                    await promise;
                }

                this.configureAutoStore();
            }
        } catch (e: any) {
            // This is a non-critical error, so catch and log only
            console.error(e);
        }
    }

    public async impersonateUserAsync(model: Mri.Web.Portal.Controllers.Account.UserModel) {
        await this.httpClient.post<Mri.Web.Portal.Controllers.Account.UserModel>(
            `/api/v1/account/impersonate/${model.id}`
        );
        this.signinRedirect({
            returnUrl: '/',
        });
    }

    public isInRole(role: string) {
        const userRoles = this.user?.roles ?? [];
        return userRoles.includes(role);
    }

    public isInRoles(roles: string[]) {
        const userRoles = this.user?.roles ?? [];
        return roles.filter(r => userRoles.includes(r)).length > 0;
    }

    @computed
    public get isTestUser() {
        return this.user?.userName == 'test';
    }

    public SetManageStore(manageStore: ManageStore) {
        this.manageStore = manageStore;
    }

    public async getAccessToken(): Promise<string | undefined> {
        let user = await this.userManager.getUser();
        if (user) {
            if (user.expired) {
                this.clearRogueOidcTokensFromLocalStorage();
                user = await this.signinSilentSafe();
            }

            return user.access_token;
        } else {
            await this.signinSilentSafe();
            return this.getAccessToken();
        }
    }

    // Clears local storage of non-user oidc tokens
    // This is to prevent a build up of rogue tokens that cause the app to fail to load
    private clearRogueOidcTokensFromLocalStorage = () => {
        const keys = Object.keys(localStorage);
        keys.forEach(key => {
            if (key.startsWith('oidc') && !key.startsWith('oidc.user')) {
                localStorage.removeItem(key);
            }
        });
    };

    private signinSilentSafe() {
        if (!this.signinSilentPromise) {
            this.signinSilentPromise = this.signinSilentRetry();
        }

        return this.signinSilentPromise;
    }

    private async signinSilentRetry() {
        let retryStep = 0;
        do {
            try {
                return await this.userManager.signinSilent();
            } catch (err) {
                console.log('Error caught while trying to sign in safely', err);
            }
        } while (retryStep++ < 4);

        throw new Error('Failed trying to signinSilent');
    }

    private configureAutoStore(useCustomStorage: boolean = true) {
        if (this.autoStoreDisposable) this.autoStoreDisposable.dispose();

        this.autoStoreDisposable = autostore(this.storage, 'AccountStore.AccountStorage', {
            useLocalStorage: true,
            useSessionStorage: false,
            useCustomStorage,
            selector: () => (this.user?.metaData.portalAccountStorage as AccountStorage) ?? undefined,
            onChange: storage => {
                if (this.user) {
                    const other = Object.assign(
                        Object.create(Object.getPrototypeOf(storage)),
                        toJS(this.user.metaData.portalAccountStorage)
                    );

                    if (!mobxEqual(storage, other)) {
                        const diffs = this.getStorageDifferences(this.user.metaData.portalAccountStorage, storage);

                        if (diffs.length > 0) {
                            runInAction(() => {
                                // Update both the user metaData and the observable storage
                                this.user!.metaData.portalAccountStorage = storage;
                                this.storage = storage; // <-- This ensures MobX reactivity
                                if(!this.suppressStorageSync) this.manageStore?.setMetaData(this.user!.metaData);
                            });
                        }
                    }
                }
            },
        });
    }

    @action
    private async tryLoadUserAsync() {
        try {
            this.userStorage.user = await this.httpClient.get<Mri.Web.Portal.Controllers.Account.UserModel>(
                '/api/v1/account'
            );
        } catch (e: any) {
            console.error(e);
        }
    }

    /**
     * Compares two AccountStorage objects to see if they are equal
     * Returns false if any of the properties are different
     * @param storage1
     * @param storage2
     * @private
     */
    @action
    private getStorageDifferences(storage1: AccountStorage, storage2: AccountStorage): string[] {
        const differences: string[] = [];

        const comparisons: [keyof AccountStorage, string][] = [
            ['activeColumnPreset', 'activeColumnPreset'],
            ['activeStickyPreset', 'activeStickyPreset'],
            ['displayAggregateColumns', 'displayAggregateColumns'],
            ['displayTableColumns', 'displayTableColumns'],
            ['displayTableConfigColumns', 'displayTableConfigColumns'],
            ['displayTableStickyColumns', 'displayTableStickyColumns'],
            ['hideUnits', 'hideUnits'],
            ['mapZoomLevel', 'mapZoomLevel'],
            ['mapShowDisplayList', 'mapShowDisplayList'],
            ['presetColumns', 'presetColumns'],
            ['presetStickyColumns', 'presetStickyColumns'],
            ['version', 'version'],
            ['viewType', 'viewType'],
            ['wrappedHeaders', 'wrappedHeaders'],
        ];

        for (const [key, name] of comparisons) {
            if (!isEqual(storage1?.[key], storage2?.[key])) {
                differences.push(name);
            }
        }
        
        return differences;
    }

    @computed
    public get user() {
        return this.userStorage.user;
    }

    @observable
    public loginState: 'login' | 'sendCode' | 'verifyCode' = 'login';
    @observable
    public storage = new AccountStorage();
    @observable
    public userStorage = new UserStorage();
    @observable
    public suppressStorageSync = false;

    @computed
    public get isLoggedIn() {
        return this.user != null;
    }

    private tokenTimer?: number;
    private autoStoreDisposable?: IDisposable;
    private signinSilentPromise?: Promise<User>;
    private manageStore?: ManageStore;
    private readonly httpClient: HttpClient;
    private readonly userManager: UserManager;
}

export const defaultTableColumns = [
    { option: 'Serial Number' },
    { option: 'Description' },
    { option: 'Status' },
    { option: 'Site ID' },
    { option: 'Display ID' },
];

export const defaultAggregateColumns = [
    { option: 'Serial Number' },
    { option: 'Display ID' },
    { option: 'Site ID' },
    { option: 'Address' },
    { option: 'City' },
    { option: 'State Or Province' },
    { option: 'Runtime Percent' },
    { option: 'Play Count' },
    { option: 'Play Duration Average' },
];

class AccountStorage {
    public static version = 9;

    public isValid() {
        return this.version == AccountStorage.version;
    }

    @observable
    public version = AccountStorage.version;
    @observable
    public mapZoomLevel = 12;
    @observable
    public mapShowDisplayList: boolean = true;
    @observable
    public contractExpirationModalDelayDate: Date | undefined = undefined;
    @observable
    public expiringAndExpiredContracts: string[] = [];
    @observable
    public viewType = DisplayViewTypes.TableLarge;
    @observable
    public wrappedHeaders: boolean = false;
    @observable
    public hideUnits: boolean = false;
    @observable
    public presetColumns: IUserColumnPreset[] = [];
    @observable
    public presetStickyColumns: IUserColumnPreset[] = [];
    @observable
    public activeColumnPreset: string = '';
    @observable
    public activeStickyPreset: string = '';
    @observable
    public displayTableStickyColumns: string[] = [];
    @observable
    public displayTableColumns: IMultiSelectOption<string | Mri.WebApi.MeasurementId>[] = defaultTableColumns.slice();
    @observable
    public displayTableConfigColumns: IMultiSelectOption<string | Mri.WebApi.MeasurementId>[] = [];
    @observable
    public displayAggregateColumns: IMultiSelectOption<string | Mri.WebApi.MeasurementId>[] =
    defaultAggregateColumns.slice();
}

class UserStorage {
    public static version = 1;

    public isValid() {
        return this.version == UserStorage.version;
    }

    @observable
    public version = UserStorage.version;
    @observable
    public user?: Mri.Web.Portal.Controllers.Account.UserModel;
}
